import * as THREE from "three";
import { cardId, isRed, type Card, type Suit } from "../game/types";
import { makeCanvasWH, mulberry32 } from "./textures";

/**
 * Faces et dos de cartes generes par canvas 2D, dans le style parchemin/bois
 * du studio (memes principes que dkg-battle). Enseignes classiques ♠♥♦♣
 * dessinees par code (cahier des charges §5, option "sure" retenue pour la
 * V1) : rouge = vin/cuivre, noir = ardoise/encre, avec valeur + enseigne dans
 * les coins haut-gauche et bas-droit pour la lecture rapide en colonne.
 */

/** Resolution de la texture d'une face (ratio 2/3, format carte a jouer vertical). */
export const CARD_TEX_WIDTH = 512;
export const CARD_TEX_HEIGHT = 768;

const RED = "#8c1f1f";
const BLACK = "#26212b";
const GOLD = "#b98d2f";

const RANK_LABELS: Record<number, string> = {
  1: "A", 11: "V", 12: "D", 13: "R",
};

function rankLabel(rank: number): string {
  return RANK_LABELS[rank] ?? String(rank);
}

function suitColor(suit: Suit): string {
  return isRed(suit) ? RED : BLACK;
}

/**
 * Fond de face de carte : jaune-beige uni et clair, pour un contraste
 * maximal avec l'encre (rouge/noir) et l'or du cadre. Un grain tres discret
 * garde un aspect papier sans reintroduire les taches sombres du parchemin
 * (qui mangeaient le contraste dans les coins).
 */
function drawCardBackground(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "#f7e7b0");
  grad.addColorStop(1, "#efdb9c");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  const rng = mulberry32(42);
  for (let i = 0; i < 90; i++) {
    ctx.globalAlpha = 0.015 + rng() * 0.02;
    ctx.fillStyle = rng() < 0.5 ? "#5a4322" : "#fffaf0";
    const r = 6 + rng() * 20;
    ctx.beginPath();
    ctx.ellipse(rng() * w, rng() * h, r, r * (0.4 + rng() * 0.6), rng() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

const faceCache = new Map<string, THREE.CanvasTexture>();
let backCache: THREE.CanvasTexture | null = null;

/** Texture de face d'une carte (mise en cache : une seule generation par carte). */
export function getCardFaceTexture(card: Pick<Card, "suit" | "rank">): THREE.CanvasTexture {
  const id = cardId(card);
  let tex = faceCache.get(id);
  if (!tex) {
    tex = new THREE.CanvasTexture(drawFace(card));
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    faceCache.set(id, tex);
  }
  return tex;
}

/** Texture du dos, commune a toutes les cartes (motif unique du Taverneu). */
export function getCardBackTexture(): THREE.CanvasTexture {
  if (!backCache) {
    backCache = new THREE.CanvasTexture(drawBack());
    backCache.colorSpace = THREE.SRGBColorSpace;
    backCache.anisotropy = 4;
  }
  return backCache;
}

// ------------------------------------------------------------------ enseignes

/**
 * Dessine une enseigne centree en (x, y), inscrite dans un carre de `size`
 * pixels. Les traces sont definis dans un repere [-50..50] puis mis a l'echelle.
 */
export function drawSuitSymbol(
  ctx: CanvasRenderingContext2D,
  suit: Suit,
  x: number,
  y: number,
  size: number,
  color?: string,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 100, size / 100);
  ctx.fillStyle = color ?? suitColor(suit);
  switch (suit) {
    case "hearts":
      ctx.beginPath();
      ctx.moveTo(0, 38);
      ctx.bezierCurveTo(-52, -4, -30, -46, 0, -16);
      ctx.bezierCurveTo(30, -46, 52, -4, 0, 38);
      ctx.fill();
      break;
    case "diamonds":
      ctx.beginPath();
      ctx.moveTo(0, -42);
      ctx.quadraticCurveTo(7, -12, 30, 0);
      ctx.quadraticCurveTo(7, 12, 0, 42);
      ctx.quadraticCurveTo(-7, 12, -30, 0);
      ctx.quadraticCurveTo(-7, -12, 0, -42);
      ctx.fill();
      break;
    case "spades":
      ctx.beginPath();
      ctx.moveTo(0, -40);
      ctx.bezierCurveTo(-50, -2, -30, 34, -4, 20);
      ctx.lineTo(4, 20);
      ctx.bezierCurveTo(30, 34, 50, -2, 0, -40);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(0, 12);
      ctx.quadraticCurveTo(-4, 30, -14, 42);
      ctx.lineTo(14, 42);
      ctx.quadraticCurveTo(4, 30, 0, 12);
      ctx.fill();
      break;
    case "clubs": {
      for (const [cx, cy] of [[0, -22], [-18, 4], [18, 4]] as const) {
        ctx.beginPath();
        ctx.arc(cx, cy, 17, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.beginPath();
      ctx.moveTo(0, 2);
      ctx.quadraticCurveTo(-4, 26, -13, 42);
      ctx.lineTo(13, 42);
      ctx.quadraticCurveTo(4, 26, 0, 2);
      ctx.fill();
      break;
    }
  }
  ctx.restore();
}

// ------------------------------------------------------------------ faces

/** Disposition standard des points par rang : [colonne, ligne, retourne]. */
const PIP_LAYOUTS: Record<number, [number, number, boolean][]> = {
  2: [[0.5, 0.16, false], [0.5, 0.84, true]],
  3: [[0.5, 0.16, false], [0.5, 0.5, false], [0.5, 0.84, true]],
  4: [[0.24, 0.16, false], [0.76, 0.16, false], [0.24, 0.84, true], [0.76, 0.84, true]],
  5: [[0.24, 0.16, false], [0.76, 0.16, false], [0.5, 0.5, false], [0.24, 0.84, true], [0.76, 0.84, true]],
  6: [[0.24, 0.16, false], [0.76, 0.16, false], [0.24, 0.5, false], [0.76, 0.5, false], [0.24, 0.84, true], [0.76, 0.84, true]],
  7: [[0.24, 0.16, false], [0.76, 0.16, false], [0.5, 0.33, false], [0.24, 0.5, false], [0.76, 0.5, false], [0.24, 0.84, true], [0.76, 0.84, true]],
  8: [[0.24, 0.16, false], [0.76, 0.16, false], [0.5, 0.33, false], [0.24, 0.5, false], [0.76, 0.5, false], [0.5, 0.67, true], [0.24, 0.84, true], [0.76, 0.84, true]],
  9: [[0.24, 0.14, false], [0.76, 0.14, false], [0.24, 0.38, false], [0.76, 0.38, false], [0.5, 0.5, false], [0.24, 0.62, true], [0.76, 0.62, true], [0.24, 0.86, true], [0.76, 0.86, true]],
  10: [[0.24, 0.14, false], [0.76, 0.14, false], [0.5, 0.26, false], [0.24, 0.38, false], [0.76, 0.38, false], [0.24, 0.62, true], [0.76, 0.62, true], [0.5, 0.74, true], [0.24, 0.86, true], [0.76, 0.86, true]],
};

function drawFace(card: Pick<Card, "suit" | "rank">): HTMLCanvasElement {
  const w = CARD_TEX_WIDTH;
  const h = CARD_TEX_HEIGHT;
  const { canvas, ctx } = makeCanvasWH(w, h);
  const color = suitColor(card.suit);

  // Graine fixe (et non plus derivee du rang/de la couleur) : meme fond
  // parchemin pour toutes les cartes, pour une lisibilite homogene en jeu.
  drawCardBackground(ctx, w, h);

  // Cadre : filet exterieur aux couleurs de l'enseigne + filet d'or interieur.
  ctx.strokeStyle = color;
  ctx.lineWidth = 10;
  roundedRect(ctx, 10, 10, w - 20, h - 20, 26);
  ctx.stroke();
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 3;
  roundedRect(ctx, 24, 24, w - 48, h - 48, 18);
  ctx.stroke();

  // Coins : valeur + petite enseigne, haut-gauche et bas-droit (retourne).
  // Gros caracteres gras (empattement par contour en plus du remplissage,
  // au-dela du poids de police disponible) : lecture immediate en eventail,
  // sans avoir a regarder le centre de la carte.
  const drawCorner = (): void => {
    const label = rankLabel(card.rank);
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineWidth = 5.5;
    ctx.lineJoin = "round";
    ctx.font = "700 100px Cinzel, Georgia, serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.strokeText(label, 72, 84);
    ctx.fillText(label, 72, 84);
    drawSuitSymbol(ctx, card.suit, 72, 170, 86);
  };
  drawCorner();
  ctx.save();
  ctx.translate(w, h);
  ctx.rotate(Math.PI);
  drawCorner();
  ctx.restore();

  if (card.rank === 1) {
    // As : grande enseigne unique au centre.
    drawSuitSymbol(ctx, card.suit, w / 2, h / 2, 180);
  } else if (card.rank <= 10) {
    drawPips(ctx, card, w, h);
  } else {
    drawFaceCard(ctx, card, w, h, color);
  }

  return canvas;
}

function drawPips(ctx: CanvasRenderingContext2D, card: Pick<Card, "suit" | "rank">, w: number, h: number): void {
  const layout = PIP_LAYOUTS[card.rank] ?? [];
  // Zone des points, en retrait des coins et des cadres.
  const x0 = 138;
  const x1 = w - 138;
  const y0 = 128;
  const y1 = h - 128;
  for (const [fx, fy, flipped] of layout) {
    const x = x0 + fx * (x1 - x0);
    const y = y0 + fy * (y1 - y0);
    if (flipped) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.PI);
      drawSuitSymbol(ctx, card.suit, 0, 0, 80);
      ctx.restore();
    } else {
      drawSuitSymbol(ctx, card.suit, x, y, 80);
    }
  }
}

/**
 * Figures (Valet/Dame/Roi) : grande lettre gravee (V/D/R) + attribut
 * distinctif (couronne, plume), directement sur le fond de la carte —
 * pas de cadre ni de fond imbriques, pour eviter un effet "carte dans la
 * carte".
 */
function drawFaceCard(
  ctx: CanvasRenderingContext2D,
  card: Pick<Card, "suit" | "rank">,
  w: number,
  h: number,
  color: string,
): void {
  const cx = w / 2;
  const cy = h / 2 + 30;

  // Grande lettre gravee, contour renforce pour rester lisible de loin.
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 6;
  ctx.lineJoin = "round";
  ctx.font = "400 230px Metamorphous, Cinzel, Georgia, serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.strokeText(rankLabel(card.rank), cx, cy + 14);
  ctx.fillText(rankLabel(card.rank), cx, cy + 14);

  // Attribut au-dessus de la lettre.
  const topY = cy - 148;
  if (card.rank === 13) {
    drawCrown(ctx, cx, topY, 150, 3);
  } else if (card.rank === 12) {
    drawCrown(ctx, cx, topY + 12, 120, 5);
  } else {
    drawFeather(ctx, cx, topY + 24);
  }
}

/** Couronne d'or a `points` pointes, base ondulee, sertie de petites pierres. */
function drawCrown(ctx: CanvasRenderingContext2D, cx: number, y: number, width: number, points: number): void {
  const h = width * 0.4;
  ctx.fillStyle = GOLD;
  ctx.beginPath();
  ctx.moveTo(cx - width / 2, y + h);
  for (let i = 0; i < points; i++) {
    const x0 = cx - width / 2 + (width * i) / points;
    const x1 = cx - width / 2 + (width * (i + 1)) / points;
    ctx.lineTo((x0 + x1) / 2, y);
    ctx.lineTo(x1, y + h * 0.55);
  }
  ctx.lineTo(cx + width / 2, y + h);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "#7a5a1a";
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fillStyle = "#8c1f1f";
  for (let i = 0; i < points; i++) {
    const x = cx - width / 2 + (width * (i + 0.5)) / points;
    ctx.beginPath();
    ctx.arc(x, y + h * 0.75, width * 0.035, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Plume de valet : hampe courbe + barbes, doree. */
function drawFeather(ctx: CanvasRenderingContext2D, cx: number, y: number): void {
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 6;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(cx - 60, y + 26);
  ctx.quadraticCurveTo(cx, y - 34, cx + 66, y - 6);
  ctx.stroke();
  ctx.lineWidth = 3;
  for (let i = 0; i < 6; i++) {
    const t = 0.2 + i * 0.13;
    const px = cx - 60 + t * 126;
    const py = y + 26 - t * 40 + t * t * 12;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px - 10, py - 22);
    ctx.stroke();
  }
  ctx.lineCap = "butt";
}

// ------------------------------------------------------------------ dos

/** Dos de carte : cuir vert bouteille, cadres d'or, embleme du Taverneu (chope). */
function drawBack(): HTMLCanvasElement {
  const w = CARD_TEX_WIDTH;
  const h = CARD_TEX_HEIGHT;
  const { canvas, ctx } = makeCanvasWH(w, h);
  const rng = mulberry32(19);

  const grad = ctx.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, "#1f4a24");
  grad.addColorStop(0.5, "#173a1c");
  grad.addColorStop(1, "#0f2a13");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // Grain de cuir.
  for (let i = 0; i < 500; i++) {
    ctx.globalAlpha = 0.03 + rng() * 0.05;
    ctx.fillStyle = rng() < 0.5 ? "#000000" : "#4a8a50";
    const r = 2 + rng() * 14;
    ctx.beginPath();
    ctx.ellipse(rng() * w, rng() * h, r, r * (0.4 + rng() * 0.6), rng() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 10;
  roundedRect(ctx, 22, 22, w - 44, h - 44, 24);
  ctx.stroke();
  ctx.lineWidth = 3;
  roundedRect(ctx, 44, 44, w - 88, h - 88, 16);
  ctx.stroke();

  // Croisillons discrets en fond (frise).
  ctx.strokeStyle = GOLD;
  ctx.globalAlpha = 0.16;
  ctx.lineWidth = 2;
  for (let x = -h; x < w + h; x += 44) {
    ctx.beginPath();
    ctx.moveTo(x, 44);
    ctx.lineTo(x + h, h - 44);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x + h, 44);
    ctx.lineTo(x, h - 44);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Embleme central : cercle d'or et chope du Taverneu.
  const cx = w / 2;
  const cy = h / 2;
  ctx.fillStyle = "#12301680";
  ctx.beginPath();
  ctx.arc(cx, cy, w * 0.24, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(cx, cy, w * 0.24, 0, Math.PI * 2);
  ctx.stroke();

  // Chope (corps + anse + mousse).
  ctx.fillStyle = GOLD;
  ctx.fillRect(cx - w * 0.07, cy - w * 0.06, w * 0.14, w * 0.16);
  ctx.strokeStyle = GOLD;
  ctx.lineWidth = 7;
  ctx.beginPath();
  ctx.arc(cx + w * 0.085, cy + w * 0.02, w * 0.05, -Math.PI / 2, Math.PI / 2);
  ctx.stroke();
  ctx.fillStyle = "#f5ecd8";
  ctx.beginPath();
  ctx.ellipse(cx, cy - w * 0.065, w * 0.085, w * 0.032, 0, 0, Math.PI * 2);
  ctx.fill();

  return canvas;
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
